// utils/validation.ts

/**
 * Validates if the provided value is a valid email address
 * @param value - The value to validate
 * @returns True if the value is a valid email, false otherwise
 */
export const isEmail = (value: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
};

/**
 * Validates if the provided value is a positive number (integer or decimal)
 * @param value - The value to validate
 * @returns True if the value is a positive number, false otherwise
 */
export const isPositiveNumber = (value: string | number): boolean => {
  const stringValue = String(value);
  return /^\d+(\.\d+)?$/.test(stringValue);
};

/**
 * Validates if the provided value is a non-empty string
 * @param value - The value to validate
 * @returns True if the value is a non-empty string, false otherwise
 */
export const isNotEmpty = (value: any): boolean => {
  return typeof value === 'string' && value.trim() !== '';
};

/**
 * Validates if the provided value is a valid number (integer or decimal)
 * Allows for partial input during typing (e.g., "123.", ".5")
 * @param value - The value to validate
 * @returns True if the value matches the numeric pattern, false otherwise
 */
export const isNumericWithDecimal = (value: string | number): boolean => {
  const stringValue = String(value);
  return /^\d*\.?\d*$/.test(stringValue);
};

/**
 * Validates that the value doesn't start with a space
 * Useful for preventing initial spaces in input fields
 * @param value - The value to validate
 * @returns True if the value doesn't start with a space, false otherwise
 */
export const preventInitialSpace = (value: any): boolean => {
  return !(typeof value === 'string' && value.length === 1 && value[0] === ' ');
};

// Optional: Create a validation result type for more complex validation
export interface ValidationResult {
  isValid: boolean;
  message?: string;
}

// Optional: Create validation rules for form fields
export interface ValidationRule {
  validate: (value: any) => boolean;
  message: string;
}

// Optional: Create a validation schema for forms
export interface FieldValidation {
  [key: string]: ValidationRule[];
}

// Optional: Create validation functions with custom error messages
export const validateEmail = (value: string): ValidationResult => {
  const isValid = isEmail(value);
  return {
    isValid,
    message: isValid ? '' : 'Please enter a valid email address'
  };
};

export const validatePositiveNumber = (value: string | number): ValidationResult => {
  const isValid = isPositiveNumber(value);
  return {
    isValid,
    message: isValid ? '' : 'Please enter a valid positive number'
  };
};

export const validateNotEmpty = (value: any, fieldName: string = 'Field'): ValidationResult => {
  const isValid = isNotEmpty(value);
  return {
    isValid,
    message: isValid ? '' : `${fieldName} is required`
  };
};

// Optional: Form validation helper
export const validateForm = <T extends Record<string, any>>(
  values: T,
  validationSchema: Record<keyof T, ValidationRule[]>
): Record<keyof T, string[]> => {
  const errors: Partial<Record<keyof T, string[]>> = {};

  (Object.keys(validationSchema) as Array<keyof T>).forEach((field) => {
    const fieldValue = values[field];
    const rules = validationSchema[field];
    
    const fieldErrors: string[] = [];
    
    rules.forEach((rule) => {
      if (!rule.validate(fieldValue)) {
        fieldErrors.push(rule.message);
      }
    });
    
    if (fieldErrors.length > 0) {
      errors[field] = fieldErrors;
    }
  });

  return errors as Record<keyof T, string[]>;
};